/**
 * requestAnimationFrame / window blur 監視の対象 window を要素から動的に解決する。
 *
 * PIP（documentPictureInPicture）でビューポート要素が別ウインドウの document へ
 * 移動しても、固定の window（オープナ側）を握ったままだと、そのオープナ側タブが
 * バックグラウンド化した際に requestAnimationFrame が停止し操作が効かなくなる。
 * 要素の ownerDocument.defaultView を都度参照することで、実際に描画されている
 * window（PIP 中はその PIP ウインドウ）へ自然に追従させる。
 */
// container は PIP（別ウインドウ）の realm で生成された要素である可能性がある
// ため、`instanceof HTMLElement` は使わない（別 realm の HTMLElement コンストラ
// クタとは一致せず false になり得る）。構造的に判定する。
export const isElementTarget = (
  target: Window | HTMLElement,
): target is HTMLElement => 'ownerDocument' in target

export const resolveAnimationWindow = (target: Window | HTMLElement): Window =>
  isElementTarget(target) ?
    (target.ownerDocument.defaultView ?? window)
  : target

/** 予約済み rAF の識別に必要な最小情報（予約先 window + 予約 ID）。 */
export interface AnimationFrameSchedule {
  readonly window: Window
  readonly frameId: number
}

/** target の現在の window で rAF を 1 件予約する。 */
export const scheduleAnimationFrame = (
  target: Window | HTMLElement,
  callback: FrameRequestCallback,
): AnimationFrameSchedule => {
  const win = resolveAnimationWindow(target)
  return { window: win, frameId: win.requestAnimationFrame(callback) }
}

/** 予約済みの rAF を、予約時の window に対してキャンセルする。 */
export const cancelScheduledAnimationFrame = (
  schedule: AnimationFrameSchedule,
): void => {
  schedule.window.cancelAnimationFrame(schedule.frameId)
}

/**
 * target の所属 window が予約時の window と異なる場合、待機中の rAF を旧 window
 * でキャンセルし、新しい window へ即座に付け替える。同じ window ならなにもせず
 * schedule をそのまま返す。
 *
 * loop コールバック内で「次フレーム到達時」に animationWindow を再評価するだけ
 * では、PIP 切替直後に旧 window（オープナ側）が既にバックグラウンド化していた
 * 場合、その旧 window では次フレーム自体が来ないため、いつまでも旧 window に
 * 留まってしまう。フォーカス取得などの契機でこの関数を呼び、即時に付け替える。
 */
export const rescheduleIfWindowChanged = (
  target: Window | HTMLElement,
  schedule: AnimationFrameSchedule,
  callback: FrameRequestCallback,
): AnimationFrameSchedule => {
  const nextWindow = resolveAnimationWindow(target)
  if (nextWindow === schedule.window) {
    return schedule
  }
  cancelScheduledAnimationFrame(schedule)
  return scheduleAnimationFrame(target, callback)
}

export interface KeyboardTargetLifecycleHandle {
  /** rAF ループ停止・focus 購読解除・blur 購読解除を行う。冪等。 */
  dispose(): void
}

/**
 * keyboardTarget に紐づく rAF ループと window blur 購読のライフサイクルを
 * まとめて管理する。
 *
 * - rAF: onFrame を毎フレーム呼び、呼び出し後に自動で次フレームを予約する。
 * - blur: target が window ではなく HTMLElement の場合、focus を得るたびに
 *   「今属している window」へ blurHandlers を張り直す。
 *
 * PIP（documentPictureInPicture）でビューポート要素が別ウインドウへ移動しても、
 * この2つが自動的に追従する。loop 内で「次フレーム到達時」に window を
 * 再評価するだけでは、PIP 切替直後に旧 window（オープナ側）が既にバック
 * グラウンド化していた場合、その旧 window ではそもそも次フレームが来ないため
 * いつまでも旧 window に留まってしまう。フォーカス取得の契機で即座に付け替える
 * ことで、この取りこぼしを防ぐ。
 *
 * @param target キー入力・フォーカスの対象（container 要素、または window）。
 * @param onFrame 毎フレーム呼ぶコールバック（次フレームの予約は自動で行われる
 *   ため、呼び出し側で再度 requestAnimationFrame する必要はない）。
 * @param blurHandlers window の blur で呼ぶハンドラ群（フォーカス喪失時の状態
 *   リセット等）。
 */
export const startKeyboardTargetLifecycle = (
  target: Window | HTMLElement,
  onFrame: FrameRequestCallback,
  blurHandlers: readonly (() => void)[],
): KeyboardTargetLifecycleHandle => {
  let disposed = false
  let schedule: AnimationFrameSchedule

  const loop: FrameRequestCallback = (now) => {
    if (disposed) {
      return
    }
    const scheduleBeforeFrame = schedule
    onFrame(now)
    // onFrame の中で dispose() や（focus 発火を伴う）target の window 移動が
    // 起き得る（例: dispose を引き起こす副作用、あるいは onFrame 内で PIP へ
    // 切り替わり同期的に focus イベントが発火するケース）。
    // - disposed だけを見て無条件に次フレームを予約すると、直前の dispose()
    //   がキャンセルした rAF を上書きする形で新規予約が漏れる。
    // - schedule が onFrame 実行中に handleFocus 経由で既に新しい window へ
    //   付け替わっている場合に、ここでも予約すると rAF ループが二重化する。
    // 呼び出し前後で schedule の同一性を見て、どちらのケースでも再予約しない。
    if (!disposed && schedule === scheduleBeforeFrame) {
      schedule = scheduleAnimationFrame(target, loop)
    }
  }
  schedule = scheduleAnimationFrame(target, loop)

  let boundBlurWindow: Window | null = null
  const rebindBlurHandlers = (): void => {
    const win = resolveAnimationWindow(target)
    if (boundBlurWindow === win) {
      return
    }
    if (boundBlurWindow) {
      for (const handler of blurHandlers) {
        boundBlurWindow.removeEventListener('blur', handler)
      }
    }
    for (const handler of blurHandlers) {
      win.addEventListener('blur', handler)
    }
    boundBlurWindow = win
  }
  rebindBlurHandlers()

  const handleFocus = (): void => {
    rebindBlurHandlers()
    schedule = rescheduleIfWindowChanged(target, schedule, loop)
  }
  if (isElementTarget(target)) {
    target.addEventListener('focus', handleFocus)
  }

  return {
    dispose: (): void => {
      if (disposed) {
        return
      }
      disposed = true
      cancelScheduledAnimationFrame(schedule)
      if (isElementTarget(target)) {
        target.removeEventListener('focus', handleFocus)
      }
      if (boundBlurWindow) {
        for (const handler of blurHandlers) {
          boundBlurWindow.removeEventListener('blur', handler)
        }
      }
    },
  }
}
